import { t } from "@travily/i18n";
import { tv, type VariantProps } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { composeRenderProps } from "@ui/primitives/shared";
import {
  Tag as PrimitiveTag,
  TagGroup as PrimitiveTagGroup,
  type TagGroupProps as PrimitiveTagGroupProps,
  TagList as PrimitiveTagList,
  type TagListProps as PrimitiveTagListProps,
  type TagProps as PrimitiveTagProps,
} from "@ui/primitives/TagGroup";
import { createContext, type ReactNode, use } from "react";

const tagGroup = tv({
  slots: {
    root: "flex flex-col gap-1",
    list: "flex flex-wrap items-center gap-1",
    tag: [
      "inline-flex max-w-fit items-center gap-1 rounded-lg whitespace-nowrap outline-none transition-colors",
      "focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-1",
      "disabled:cursor-not-allowed disabled:opacity-50",
    ],
    removeButton: [
      "inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full p-0.5",
      "text-current opacity-70 hover:opacity-100 hover:bg-black/10 outline-none",
      "focus-visible:ring-2 focus-visible:ring-blue-300",
    ],
  },
  variants: {
    color: {
      yellow: { tag: "bg-yellow-200 text-yellow-900" },
      blue: { tag: "bg-blue-100 text-blue-900" },
      green: { tag: "bg-green-100 text-green-900" },
      neutral: { tag: "bg-neutral-100 text-neutral-700" },
    },
    size: {
      sm: { tag: "h-6 px-2 text-xs leading-5", removeButton: "[&_svg]:size-3" },
      md: { tag: "h-7 px-2 text-sm leading-5", removeButton: "[&_svg]:size-3.5" },
      lg: { tag: "h-8 px-2 text-base leading-6", removeButton: "[&_svg]:size-4" },
      xl: { tag: "h-9 px-2.5 text-base leading-6", removeButton: "[&_svg]:size-4" },
    },
  },
  defaultVariants: { color: "neutral", size: "lg" },
});

type TagGroupVariants = VariantProps<typeof tagGroup>;
export type TagGroupColor = NonNullable<TagGroupVariants["color"]>;
export type TagGroupSize = NonNullable<TagGroupVariants["size"]>;

interface TagGroupContextValue {
  color: TagGroupColor;
  size: TagGroupSize;
}

const TagGroupContext = createContext<TagGroupContextValue>({ color: "neutral", size: "lg" });

export interface TagGroupProps<T extends object>
  extends Omit<PrimitiveTagGroupProps, "children" | "className">,
    Pick<PrimitiveTagListProps<T>, "items" | "children" | "renderEmptyState"> {
  className?: string;
  color?: TagGroupColor;
  size?: TagGroupSize;
}

function TagGroupRoot<T extends object>({
  color = "neutral",
  size = "lg",
  items,
  children,
  renderEmptyState,
  className,
  ...props
}: TagGroupProps<T>) {
  const styles = tagGroup({ color, size });

  return (
    <TagGroupContext value={{ color, size }}>
      <PrimitiveTagGroup {...props} className={styles.root({ className })}>
        <PrimitiveTagList className={styles.list()} items={items} renderEmptyState={renderEmptyState}>
          {children}
        </PrimitiveTagList>
      </PrimitiveTagGroup>
    </TagGroupContext>
  );
}

export interface TagItemProps extends Omit<PrimitiveTagProps, "children"> {
  children: ReactNode;
  color?: TagGroupColor;
}

function TagItem({ children, color: colorProp, ...props }: TagItemProps) {
  const { color: contextColor, size } = use(TagGroupContext);
  const color = colorProp ?? contextColor;
  const styles = tagGroup({ color, size });
  const textValue = typeof children === "string" ? children : undefined;

  return (
    <PrimitiveTag
      textValue={textValue}
      {...props}
      className={composeRenderProps(props.className, (className) => styles.tag({ className }))}
    >
      {composeRenderProps(children, (resolvedChildren, { allowsRemoving }) => (
        <>
          <span className="truncate">{resolvedChildren}</span>
          {allowsRemoving && (
            <Button aria-label={t("components.tag_group.remove")} className={styles.removeButton()} slot="remove">
              <svg
                aria-hidden="true"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path d="M6 6 18 18M6 18 18 6" />
              </svg>
            </Button>
          )}
        </>
      ))}
    </PrimitiveTag>
  );
}

export const TagGroup = Object.assign(TagGroupRoot, { Tag: TagItem });
